import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '../components/ui/command';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../components/ui/tooltip';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend, Area, AreaChart, Cell
} from 'recharts';
import {
  Loader2, TrendingUp, TrendingDown, Minus, DollarSign, FileText,
  Receipt, ChevronsUpDown, Check, RotateCcw, Info, ArrowUpDown,
  ChevronUp, ChevronDown, Building2, Package, UserCheck, Layers, Users
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import PeriodPresets, { PERIOD_PRESETS } from '../components/PeriodPresets';

const fmt = (v) => v != null ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v) : '-';
const fmtPct = (v) => v != null ? `${v > 0 ? '+' : ''}${v}%` : '-';
const fmtN = (v) => v != null ? new Intl.NumberFormat('it-IT').format(v) : '-';

export default function SalesDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters - default: Anno corrente
  const currentYearPreset = PERIOD_PRESETS.find(p => p.value === 'current_year');
  const [periodPreset, setPeriodPreset] = useState('current_year');
  const [startDate, setStartDate] = useState(() => currentYearPreset?.getRange().start || '');
  const [endDate, setEndDate] = useState(() => currentYearPreset?.getRange().end || '');
  const [buIds, setBuIds] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [amIds, setAmIds] = useState([]);
  const [clientIds, setClientIds] = useState([]);
  const [settoreIds, setSettoreIds] = useState([]);
  const [customFieldId, setCustomFieldId] = useState('');
  const [groupRenewals, setGroupRenewals] = useState(false);
  const [cfFilters, setCfFilters] = useState({});

  const hasFilters = startDate || endDate || buIds.length || productIds.length || amIds.length || clientIds.length || settoreIds.length || Object.values(cfFilters).some(v => v?.length);

  const handlePresetChange = (presetValue) => {
    setPeriodPreset(presetValue);
    if (presetValue === 'all') {
      setStartDate(''); setEndDate('');
    } else if (presetValue !== 'custom') {
      const preset = PERIOD_PRESETS.find(p => p.value === presetValue);
      if (preset) {
        const { start, end } = preset.getRange();
        setStartDate(start); setEndDate(end);
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (buIds.length) params.business_unit_id = buIds.join(',');
      if (productIds.length) params.product_id = productIds.join(',');
      if (amIds.length) params.account_manager_id = amIds.join(',');
      if (clientIds.length) params.client_id = clientIds.join(',');
      if (settoreIds.length) params.settore = settoreIds.join(',');
      if (customFieldId) params.custom_field_id = customFieldId;
      if (groupRenewals) params.group_renewals = true;
      const cfArr = Object.entries(cfFilters).filter(([,v]) => v?.length).map(([k,v]) => ({ field_id: k, values: v }));
      if (cfArr.length > 0) params.custom_field_filters = JSON.stringify(cfArr);
      const res = await api.get('/dashboard/vendite', { params });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [startDate, endDate, buIds, productIds, amIds, clientIds, settoreIds, customFieldId, groupRenewals, cfFilters]);

  // Auto-select first custom field when data loads
  useEffect(() => {
    const ccf = data?.filters_meta?.contract_custom_fields || [];
    if (ccf.length > 0 && !customFieldId) {
      setCustomFieldId(ccf[0].id);
    }
  }, [data?.filters_meta?.contract_custom_fields]);

  const resetFilters = () => {
    setPeriodPreset('all'); setStartDate(''); setEndDate(''); setBuIds([]); setProductIds([]); setAmIds([]); setClientIds([]); setSettoreIds([]); setCfFilters({});
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const trend = data?.trend || [];
  const buDist = data?.distribuzione_bu || [];
  const prodDist = data?.distribuzione_prodotto || [];
  const clientDist = data?.distribuzione_clienti || [];
  const cfDist = data?.distribuzione_custom_field || [];
  const amRanking = data?.ranking_am || [];
  const meta = data?.filters_meta || {};
  const contractCustomFields = meta.contract_custom_fields || [];

  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* SECTION 1: Filters */}
        <Card data-testid="sales-filters">
          <CardContent className="py-3 px-4">
            {/* Period presets row */}
            <PeriodPresets
              activePreset={periodPreset}
              onPresetChange={handlePresetChange}
              showCustomDates={periodPreset === 'custom'}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(v) => { setStartDate(v); setPeriodPreset('custom'); }}
              onEndDateChange={(v) => { setEndDate(v); setPeriodPreset('custom'); }}
              testIdPrefix="sales-period"
            />
            {/* Other filters row */}
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <MultiSearchFilter label="Business Unit" values={buIds} onChange={setBuIds}
                options={meta.business_units || []} testId="filter-bu" />
              <MultiSearchFilter label="Prodotto" values={productIds} onChange={setProductIds}
                options={meta.products || []} testId="filter-product" />
              <MultiSearchFilter label="Account Manager" values={amIds} onChange={setAmIds}
                options={meta.account_managers || []} testId="filter-am" />
              <MultiSearchFilter label="Cliente" values={clientIds} onChange={setClientIds}
                options={meta.clients || []} testId="filter-client" />
              <MultiSearchFilter label="Settore" values={settoreIds} onChange={setSettoreIds}
                options={(meta.settori || []).map(s => ({ id: s, name: s }))} testId="filter-settore" />
              {contractCustomFields.map(cf => {
                const opts = (cf.options || []).map(o => ({ id: o, name: o }));
                const hasRinnovi = opts.some(o => /rinnovo/i.test(o.name));
                const extraOpts = [];
                if (hasRinnovi) extraOpts.push({ id: '__all_renewals__', name: 'Tutti i rinnovi' });
                extraOpts.push({ id: '__unspecified__', name: 'Non specificato' });
                const finalOpts = [...extraOpts, ...opts];
                return (
                  <MultiSearchFilter key={cf.id} label={cf.name} values={cfFilters[cf.id] || []}
                    onChange={v => setCfFilters(prev => {
                      const next = { ...prev };
                      if (v.length) next[cf.id] = v; else delete next[cf.id];
                      return next;
                    })}
                    options={finalOpts}
                    testId={`filter-cf-${cf.id}`} />
                );
              })}
              {hasFilters && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500 gap-1"
                  onClick={resetFilters} data-testid="filter-reset">
                  <RotateCcw className="w-3 h-3" /> Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SECTION 2: KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kpi-section">
          <KPICard
            title="Venduto Totale"
            value={fmt(kpi.venduto_totale)}
            delta={kpi.delta_venduto}
            icon={<DollarSign className="w-4 h-4" />}
            sparkData={kpi.mini_trend?.map(t => t.venduto) || []}
            tooltip="Somma degli importi dei contratti nel periodo selezionato"
            testId="kpi-venduto"
            color="orange"
          />
          <KPICard
            title="Numero Contratti"
            value={fmtN(kpi.num_contratti)}
            delta={kpi.delta_contratti}
            icon={<FileText className="w-4 h-4" />}
            sparkData={kpi.mini_trend?.map(t => t.contratti) || []}
            tooltip="Numero di contratti firmati nel periodo selezionato"
            testId="kpi-contratti"
            color="blue"
          />
          <KPICard
            title="Ticket Medio"
            value={fmt(kpi.ticket_medio)}
            delta={kpi.delta_ticket}
            icon={<Receipt className="w-4 h-4" />}
            sparkData={kpi.mini_trend?.map(t => t.contratti > 0 ? Math.round(t.venduto / t.contratti) : 0) || []}
            tooltip="Venduto Totale / Numero Contratti"
            testId="kpi-ticket"
            color="emerald"
          />
        </div>

        {/* SECTION 3: Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="trend-section">
          <Card>
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700">Trend Venduto</CardTitle>
            </CardHeader>
            <CardContent className="pb-4 px-3">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gradVenduto" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#999" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#999" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <RechartsTooltip formatter={(v) => [fmt(v), 'Venduto']} labelFormatter={l => `Mese: ${l}`} />
                  <Area type="monotone" dataKey="venduto" stroke="#f97316" strokeWidth={2.5}
                    fill="url(#gradVenduto)" dot={{ r: 4, fill: '#f97316' }} activeDot={{ r: 6 }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-semibold text-gray-700">Trend Contratti</CardTitle>
            </CardHeader>
            <CardContent className="pb-4 px-3">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#999" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#999" allowDecimals={false} />
                  <RechartsTooltip formatter={(v) => [v, 'Contratti']} labelFormatter={l => `Mese: ${l}`} />
                  <Bar dataKey="contratti" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* SECTION 4: Distributions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="distribution-section">
          <DistributionTable
            title="Distribuzione per Business Unit"
            icon={<Building2 className="w-4 h-4 text-orange-500" />}
            data={buDist}
            testId="dist-bu"
            initialLimit={10}
          />
          <DistributionTable
            title="Distribuzione per Prodotto"
            icon={<Package className="w-4 h-4 text-blue-500" />}
            data={prodDist}
            testId="dist-product"
            initialLimit={10}
          />
          <DistributionTable
            title="Distribuzione per Cliente"
            icon={<Users className="w-4 h-4 text-emerald-500" />}
            data={clientDist}
            testId="dist-client"
            initialLimit={10}
          />
        </div>

        {/* SECTION 4b: Custom Field Distribution */}
        {contractCustomFields.length > 0 && (
          <Card data-testid="dist-custom-field">
            <CardHeader className="pb-2 pt-4 px-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-violet-500" /> Distribuzione per Campo Personalizzato
                </CardTitle>
                <div className="flex items-center gap-3">
                  <select
                    value={customFieldId}
                    onChange={e => setCustomFieldId(e.target.value)}
                    className="h-8 text-xs border rounded-md px-2 bg-white min-w-[160px]"
                    data-testid="custom-field-select"
                  >
                    {contractCustomFields.map(cf => (
                      <option key={cf.id} value={cf.id}>{cf.name}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="group-renewals-toggle">
                    <div className={`relative w-9 h-5 rounded-full transition-colors ${groupRenewals ? 'bg-violet-500' : 'bg-gray-200'}`}
                      onClick={() => setGroupRenewals(!groupRenewals)}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${groupRenewals ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-[11px] text-gray-500 font-medium">Raggruppa rinnovi</span>
                  </label>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-2">
              {cfDist.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  {customFieldId ? 'Nessun dato per questo campo nel periodo selezionato' : 'Seleziona un campo personalizzato'}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Chart */}
                  <div className="px-4 pb-4">
                    <ResponsiveContainer width="100%" height={Math.max(200, cfDist.length * 36)}>
                      <BarChart data={cfDist} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10 }} stroke="#999" tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#999" width={110} />
                        <RechartsTooltip formatter={(v) => [fmt(v), 'Venduto']} />
                        <Bar dataKey="venduto" radius={[0, 4, 4, 0]} maxBarSize={28}>
                          {cfDist.map((_, i) => {
                            const colors = ['#8b5cf6', '#f97316', '#3b82f6', '#10b981', '#ef4444', '#ec4899', '#06b6d4', '#eab308'];
                            return <Cell key={i} fill={colors[i % colors.length]} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left px-4 py-2 text-gray-400 font-medium">Tipologia</th>
                          <th className="text-right px-3 py-2 text-gray-400 font-medium">Venduto</th>
                          <th className="text-right px-3 py-2 text-gray-400 font-medium">Contratti</th>
                          <th className="text-right px-3 py-2 text-gray-400 font-medium">Ticket Medio</th>
                          <th className="text-right px-3 py-2 text-gray-400 font-medium">Incidenza</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cfDist.map((row, i) => (
                          <tr key={row.name} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 font-medium text-gray-700 flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{
                                backgroundColor: ['#8b5cf6', '#f97316', '#3b82f6', '#10b981', '#ef4444', '#ec4899', '#06b6d4', '#eab308'][i % 8]
                              }} />
                              {row.name}
                            </td>
                            <td className="text-right px-3 py-2.5 font-semibold text-gray-800">{fmt(row.venduto)}</td>
                            <td className="text-right px-3 py-2.5 text-gray-600">{row.contratti}</td>
                            <td className="text-right px-3 py-2.5 text-gray-600">{fmt(row.ticket_medio)}</td>
                            <td className="text-right px-3 py-2.5">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-violet-400 rounded-full" style={{ width: `${Math.min(row.incidenza, 100)}%` }} />
                                </div>
                                <span className="text-gray-600 w-10 text-right">{row.incidenza}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SECTION 5: AM Ranking */}
        <AMRankingTable data={amRanking} testId="ranking-am" />
      </div>
    </TooltipProvider>
  );
}

/* ==================== KPI Card ==================== */
function KPICard({ title, value, delta, icon, sparkData, tooltip, testId, color }) {
  const colors = {
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', spark: '#f97316' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', spark: '#3b82f6' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', spark: '#10b981' },
  }[color] || { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', spark: '#6b7280' };

  const deltaColor = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-gray-400';
  const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;

  // Mini sparkline using simple SVG
  const sparkW = 60, sparkH = 24;
  const max = Math.max(...sparkData, 1);
  const min = Math.min(...sparkData, 0);
  const range = max - min || 1;
  const points = sparkData.map((v, i) =>
    `${(i / (sparkData.length - 1 || 1)) * sparkW},${sparkH - ((v - min) / range) * sparkH}`
  ).join(' ');

  return (
    <Card className={`${colors.border} border overflow-hidden`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg ${colors.bg} flex items-center justify-center ${colors.icon}`}>
              {icon}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</span>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-3 h-3 text-gray-300" />
                </TooltipTrigger>
                <TooltipContent side="top"><p className="text-xs max-w-[200px]">{tooltip}</p></TooltipContent>
              </Tooltip>
            </div>
          </div>
          {sparkData.length > 1 && (
            <svg width={sparkW} height={sparkH} className="mt-1 opacity-50">
              <polyline fill="none" stroke={colors.spark} strokeWidth="1.5" points={points} />
            </svg>
          )}
        </div>
        <div className="flex items-end justify-between">
          <span className="text-2xl font-bold text-gray-900 tracking-tight" data-testid={`${testId}-value`}>
            {value}
          </span>
          {delta != null && (
            <div className={`flex items-center gap-0.5 text-xs font-medium ${deltaColor}`} data-testid={`${testId}-delta`}>
              <DeltaIcon className="w-3.5 h-3.5" />
              {fmtPct(delta)}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ==================== Distribution Table ==================== */
function DistributionTable({ title, icon, data, testId, initialLimit }) {
  const [sortKey, setSortKey] = useState('venduto');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => sortDir === 'desc' ? b[sortKey] - a[sortKey] : a[sortKey] - b[sortKey]);
  }, [data, sortKey, sortDir]);

  const hasMore = initialLimit && sorted.length > initialLimit;
  const visible = (initialLimit && !expanded) ? sorted.slice(0, initialLimit) : sorted;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />;
  };

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          {icon} {title}
          {data.length > 0 && <span className="text-[10px] font-normal text-gray-400">({data.length})</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-2 text-gray-400 font-medium">Nome</th>
                {[
                  ['venduto', 'Venduto'],
                  ['contratti', 'N.'],
                  ['ticket_medio', 'Ticket'],
                  ['incidenza', '%'],
                ].map(([k, label]) => (
                  <th key={k} className="text-right px-3 py-2 text-gray-400 font-medium cursor-pointer select-none hover:text-gray-600"
                    onClick={() => toggleSort(k)}>
                    <div className="flex items-center justify-end gap-1">
                      {label} <SortIcon k={k} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-6 text-gray-400">Nessun dato</td></tr>
              ) : visible.map((row, i) => (
                <tr key={row.id || i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-2 font-medium text-gray-700 truncate max-w-[150px]" title={row.name}>{row.name}</td>
                  <td className="text-right px-3 py-2 font-semibold text-gray-800">{fmt(row.venduto)}</td>
                  <td className="text-right px-3 py-2 text-gray-600">{row.contratti}</td>
                  <td className="text-right px-3 py-2 text-gray-600">{fmt(row.ticket_medio)}</td>
                  <td className="text-right px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.min(row.incidenza, 100)}%` }} />
                      </div>
                      <span className="text-gray-600 w-9 text-right">{row.incidenza}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full py-2 text-[11px] font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50/50 transition-colors"
            data-testid={`${testId}-toggle-expand`}
          >
            {expanded ? 'Mostra meno' : `Mostra tutti (${sorted.length})`}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

/* ==================== AM Ranking Table ==================== */
function AMRankingTable({ data, testId }) {
  const [sortKey, setSortKey] = useState('venduto');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />;
  };

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-amber-500" /> Ranking Account Manager
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-2">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-2 text-gray-400 font-medium w-8">#</th>
                <th className="text-left px-3 py-2 text-gray-400 font-medium">Nome</th>
                {[
                  ['venduto', 'Venduto'],
                  ['contratti', 'Contratti'],
                  ['ticket_medio', 'Ticket Medio'],
                  ['incidenza', 'Incidenza %'],
                  ['delta_pct', 'Delta %'],
                ].map(([k, label]) => (
                  <th key={k} className="text-right px-3 py-2 text-gray-400 font-medium cursor-pointer select-none hover:text-gray-600"
                    onClick={() => toggleSort(k)}>
                    <div className="flex items-center justify-end gap-1">
                      {label} <SortIcon k={k} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-6 text-gray-400">Nessun dato</td></tr>
              ) : sorted.map((row, i) => (
                <tr key={row.id || i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-2.5 text-gray-400 font-medium">{i + 1}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-[10px] font-bold">
                        {row.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="font-medium text-gray-700">{row.name}</span>
                    </div>
                  </td>
                  <td className="text-right px-3 py-2.5 font-semibold text-gray-800">{fmt(row.venduto)}</td>
                  <td className="text-right px-3 py-2.5 text-gray-600">{row.contratti}</td>
                  <td className="text-right px-3 py-2.5 text-gray-600">{fmt(row.ticket_medio)}</td>
                  <td className="text-right px-3 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${Math.min(row.incidenza, 100)}%` }} />
                      </div>
                      <span className="text-gray-600 w-10 text-right">{row.incidenza}%</span>
                    </div>
                  </td>
                  <td className="text-right px-3 py-2.5">
                    {row.delta_pct != null ? (
                      <span className={`font-medium ${row.delta_pct > 0 ? 'text-emerald-600' : row.delta_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                        {fmtPct(row.delta_pct)}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ==================== Multi Search Filter ==================== */
function MultiSearchFilter({ label, values, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const selectedNames = options.filter(o => values.includes(o.id)).map(o => o.name);
  const displayText = selectedNames.length === 0 ? 'Tutti' : selectedNames.length <= 2 ? selectedNames.join(', ') : `${selectedNames.length} selezionati`;

  const toggle = (id) => {
    if (values.includes(id)) {
      onChange(values.filter(v => v !== id));
    } else {
      onChange([...values, id]);
    }
  };

  return (
    <div className="space-y-1 min-w-[140px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={`h-8 text-xs w-full justify-between font-normal px-3 ${values.length > 0 ? 'border-orange-300 bg-orange-50/50' : ''}`} data-testid={testId}>
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca..." className="h-8 text-xs" />
            <CommandList>
              <CommandEmpty>Nessun risultato</CommandEmpty>
              <CommandGroup>
                {values.length > 0 && (
                  <CommandItem value="__clear__" onSelect={() => onChange([])}>
                    <RotateCcw className="mr-2 h-3 w-3 text-gray-400" />
                    <span className="text-gray-500">Deseleziona tutto</span>
                  </CommandItem>
                )}
                {options.map(o => (
                  <CommandItem key={o.id} value={o.name} onSelect={() => toggle(o.id)}>
                    <div className={`mr-2 h-3.5 w-3.5 rounded border flex items-center justify-center shrink-0 ${
                      values.includes(o.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                    }`}>
                      {values.includes(o.id) && <Check className="h-2.5 w-2.5 text-white" />}
                    </div>
                    {o.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
